import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { usePurchasesQuery } from "@/partner/entities/purchases";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat, useAbility } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { formatAmountObject } from "@/partner/features/products";
import { PagePaths } from "@/partner/shared/constants/routes";
import { BuyerEmailCell } from "@/partner/shared/components/BuyerEmailCell";
import { buildPurchasesFilter } from "../model/buildPurchasesFilter";
import { buildPaymentBreakdownRows } from "../model/paymentBreakdown";
import { PurchaseDetailsDialog } from "./PurchaseDetailsDialog";
import { RenewStatus } from "./RenewStatus";
import { TerminatePurchaseButton } from "./TerminatePurchaseButton";
import { EditPurchaseDatesButton } from "./EditPurchaseDatesButton";
import type { PurchasesListQueryType } from "@/types/graphql/partner.generated";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";

type PurchaseRow = PurchasesListQueryType["productPurchases"]["nodes"][number];

type PurchaseStatus = "active" | "expired" | "refunded" | "disputed";

const STATUS_COLOR: Record<PurchaseStatus, "success" | "default" | "error"> = {
  active: "success",
  expired: "default",
  refunded: "error",
  disputed: "error",
};

// Ключи локализации типа продукта (products.type.ppv/subscription) — тот же
// маппинг, что в RefundsList и PurchasesFilter.
const TYPE_KEY: Record<string, string> = {
  PPV: "ppv",
  SUBSCRIPTION: "subscription",
};

// Референс: ProductPurchasesList — статус вычисляется на клиенте (в схеме
// нет отдельного enum-поля status), приоритет refunded > disputed > expired.
function computeStatus(row: {
  refunded: boolean;
  disputed: boolean;
  expiredAt: string;
}): PurchaseStatus {
  if (row.refunded) return "refunded";
  if (row.disputed) return "disputed";
  if (new Date(row.expiredAt) < new Date()) return "expired";
  return "active";
}

export const PurchasesList: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error } = usePurchasesQuery({
    // page сбрасывается в 0 самим FilterProvider при любом изменении
    // filter[...] (см. changeParams), отдельного сброса здесь не нужно.
    variables: { page, rowsPerPage, filter: buildPurchasesFilter(filter) },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.productPurchases?.nodes ?? [];
  const count = data?.productPurchases?.count ?? 0;

  // Диалог деталей открывается локальным useState (не URL-параметром, как
  // `?tab=edit&id=` у Products): этот список read-only, ссылку на конкретную
  // покупку никто не шэрит, поэтому URL-состояние избыточно.
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseRow | null>(
    null
  );

  const ability = useAbility();
  const canManagePurchase = ability.can("update", "ProductPurchase");

  const emptyValue = t("common.emptyDash");

  const formatAmount = (row: PurchaseRow): string =>
    formatAmountObject(row.payment?.amount, emptyValue);

  const formatOrganizationAmount = (row: PurchaseRow): string =>
    formatAmountObject(
      row.payment?.organizationAmountInOrganizationCurrency,
      emptyValue
    );

  // Подсказка у колонки «Ваш доход»: та же раскладка, что в диалоге деталей,
  // одной строкой. Значение самой ячейки не меняется.
  const formatBreakdownTooltip = (row: PurchaseRow): string =>
    row.payment
      ? buildPaymentBreakdownRows(row.payment)
          .map(
            ({ key, amount }) =>
              `${t(`purchases.details.${key}`)}: ${formatAmountObject(amount, emptyValue)}`
          )
          .join(" · ")
      : "";

  const formatProvider = (row: PurchaseRow): string =>
    row.payment
      ? t(PAYMENT_PROVIDER_LABEL_KEY[row.payment.provider])
      : emptyValue;

  const formatPeriod = (row: PurchaseRow): string =>
    row.payment
      ? t("purchases.list.periodDays", { count: row.payment.period })
      : emptyValue;

  const formatStatus = (row: PurchaseRow): string =>
    t(`purchases.status.${computeStatus(row)}`);

  const formatType = (row: PurchaseRow): string =>
    t(`products.type.${TYPE_KEY[row.type]}`);

  const formatDate = (value: string, format: "short" | "time"): string =>
    interpolationFormat(value, format, i18n.language);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      // Референс partner/components/TableLoading/TableLoading.js: блок
      // загрузки занимает фиксированные 300px, чтобы таблица не прыгала.
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("purchases.list.empty")} />;
  }

  return (
    <>
      <Card elevation={0} sx={{ overflowX: "auto" }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>{t("purchases.list.amountColumn")}</TableCell>
              <TableCell>
                {t("purchases.list.organizationAmountColumn")}
              </TableCell>
              <TableCell>{t("purchases.list.providerColumn")}</TableCell>
              <TableCell>{t("purchases.list.buyerColumn")}</TableCell>
              <TableCell sx={{ minWidth: 240 }}>
                {t("purchases.list.productColumn")}
              </TableCell>
              <TableCell>{t("purchases.list.renewColumn")}</TableCell>
              <TableCell>{t("purchases.list.statusColumn")}</TableCell>
              <TableCell>{t("purchases.list.periodColumn")}</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>
                {t("purchases.list.createdAtColumn")}
              </TableCell>
              <TableCell align="right" sx={{ width: 160 }}>
                {t("purchases.list.expiredAtColumn")}
              </TableCell>
              {canManagePurchase && (
                <TableCell align="right">
                  {t("common.actions.actions")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const status = computeStatus(row);

              return (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedPurchase(row)}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {/* Референс ProductPurchasesList.js:131-147: сумма это
                          ссылка на деталку, зелёная у активной покупки и
                          красная у возврата или диспута. */}
                      <MuiLink
                        component="button"
                        type="button"
                        variant="body2"
                        underline="hover"
                        sx={{
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          color:
                            row.refunded || row.disputed
                              ? "error.main"
                              : "success.main",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedPurchase(row);
                        }}
                      >
                        {formatAmount(row)}
                      </MuiLink>
                      {row.subscription?.renewSubscription && (
                        <Tooltip title={t("purchases.list.autoRenewalTooltip")}>
                          <AutorenewIcon
                            color="disabled"
                            fontSize="small"
                            sx={{ ml: 0.5 }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={formatBreakdownTooltip(row)}>
                      <Typography variant="body2" noWrap>
                        {formatOrganizationAmount(row)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatProvider(row)}
                    </Typography>
                  </TableCell>

                  {/* stopPropagation: клик по ссылке уводит на страницу
                      пользователя или продукта, диалог деталей строки при
                      этом открываться не должен. */}
                  <TableCell sx={{ maxWidth: 220 }}>
                    <BuyerEmailCell
                      email={row.buyerEmail}
                      userId={row.userId}
                      emptyValue={emptyValue}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 240 }}>
                    <MuiLink
                      component={RouterLink}
                      color="textPrimary"
                      target="_blank"
                      to={`${PagePaths.products}?tab=edit&id=${row.product.id}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.product.title}
                    </MuiLink>
                    <Typography
                      variant="caption"
                      component="div"
                      color="textSecondary"
                      noWrap
                    >
                      {formatType(row)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <RenewStatus subscription={row.subscription} />
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_COLOR[status]}
                      label={formatStatus(row)}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatPeriod(row)}
                    </Typography>
                  </TableCell>

                  {/* Референс ProductPurchasesList.js:239-277: сверху время
                      основным цветом, снизу дата вторичным. */}
                  <TableCell align="right" sx={{ width: 160 }}>
                    <Typography variant="body2" color="textPrimary" noWrap>
                      {row.payment
                        ? formatDate(row.payment.createdAt, "time")
                        : emptyValue}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {row.payment
                        ? formatDate(row.payment.createdAt, "short")
                        : ""}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ width: 160 }}>
                    <Typography variant="body2" color="textPrimary" noWrap>
                      {formatDate(row.expiredAt, "time")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {formatDate(row.expiredAt, "short")}
                    </Typography>
                  </TableCell>

                  {canManagePurchase && (
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        <TerminatePurchaseButton
                          id={row.id}
                          expiredAt={row.expiredAt}
                        />
                        <EditPurchaseDatesButton
                          id={row.id}
                          startAt={row.startAt}
                          expiredAt={row.expiredAt}
                        />
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 100]}
          labelRowsPerPage={t("common.pagination.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            t("common.pagination.displayedRows", {
              from,
              to,
              count:
                count !== -1 ? count : t("common.pagination.moreThan", { to }),
            })
          }
        />
      </Card>

      <PurchaseDetailsDialog
        open={Boolean(selectedPurchase)}
        purchase={selectedPurchase}
        onClose={() => setSelectedPurchase(null)}
      />
    </>
  );
};
